import { Component, ChangeDetectorRef, EventEmitter, Input, OnInit, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators,
  FormsModule,
} from '@angular/forms';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { DropdownSelectComponent } from '../../dropdown-select/dropdown-select.component';
import { MultiselectComponent } from '../../multiselect/multiselect.component';
import { ButtonComponent } from '../../button/button.component';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { SharedService } from '../../../services/shared.service';

interface SurveyOption {
  qlabel?: Record<string, string>;
  label?: Record<string, string>;
  qvalue?: string;
  value?: string;
  dsLabel?: string;
}

interface SurveyQuestion {
  qid: string;
  qtype: string;
  qlabel: Record<string, string>;
  placeholder?: Record<string, string>;
  options?: SurveyOption[];
  required: boolean;
  parentQId: string | null;
  visibleif?: { qid: string; value: string; operator: string };
  validation?: {
    maxLength?: string;
    minLength?: string;
    format?: string;
    pattern?: string;
    minAge?: number;
    maxAge?: number;
    minDate?: string;
    maxDate?: string;
  };
}

interface SurveyHistoryQuestion extends SurveyQuestion {
  answer?: string | string[] | null;
  answerLabel?: string | null;
  answerLabels?: string | string[] | null;
  dsLabel?: string;
  wasVisible?: boolean;
}

interface SurveyData {
  recordId: string;
  srvyId: number;
  userType: string;
  srvyMsg: string;
  srvyQuestions: string;
}


@Component({
  selector: 'ppl-survey-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzFormModule,
    NzRadioModule,
    NzInputModule,
    NzCheckboxModule,
    FormsModule,
    ButtonComponent,
    DropdownSelectComponent,
    MultiselectComponent,
  ],
  templateUrl: './survey-form.component.html',
  styleUrl: './survey-form.component.css',
})
export class SurveyFormComponent implements OnInit, OnChanges {
  form!: FormGroup;
  parsedQuestions: SurveyQuestion[] = [];
  submittedQids = new Set<string>();

  @Input() surveyData: SurveyData | undefined;
  @Input() mode: 'add' | 'edit' | 'read' = 'add';
  @Input() historyEntry: { srvySubmitDate: string; srvyId: number; srvyRespJson: string } | undefined;
  @Output() surveySubmitted = new EventEmitter<void>();

  constructor(private fb: FormBuilder, private sharedService: SharedService, private cdr: ChangeDetectorRef) {}

  ngOnInit() {
    if (this.mode !== 'read') {
      this.parsedQuestions = JSON.parse(this.surveyData!.srvyQuestions);
      this.form = this.fb.group({});
      this.buildForm();
      this.form.valueChanges.subscribe(() => this.handleConditionalLogic());
      if (this.mode === 'edit' && this.historyEntry) {
        this.initFromHistoryEntry(this.historyEntry);
      }
    } else {
      this.form = this.fb.group({});
      if (this.historyEntry) {
        this.initFromHistoryEntry(this.historyEntry);
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['historyEntry'] && !changes['historyEntry'].firstChange && this.historyEntry) {
      this.initFromHistoryEntry(this.historyEntry);
    }
  }

  private initFromHistoryEntry(entry: { srvyRespJson: string }): void {
    const questions = (JSON.parse(entry.srvyRespJson) as Record<string, unknown>[])
      .map(q => this.normalizeHistoryQuestion(q));
    this.parsedQuestions = questions;
    this.form = this.fb.group({});
    this.buildForm();
    this.prefillFormFromQuestions(questions);
    if (this.mode === 'read') {
      this.form.disable();
    }
    this.cdr.detectChanges();
  }

  private normalizeHistoryQuestion(raw: Record<string, unknown>): SurveyHistoryQuestion {
    const rawOptions = raw['options'] as Record<string, unknown>[] | undefined;
    return {
      qid: raw['qid'] as string,
      qtype: raw['qtype'] as string,
      qlabel: (raw['qlabel'] ?? raw['label'] ?? {}) as Record<string, string>,
      required: (raw['required'] as boolean) ?? false,
      parentQId: (raw['parentQId'] as string | null) ?? null,
      visibleif: raw['visibleif'] as SurveyQuestion['visibleif'],
      wasVisible: raw['wasVisible'] as boolean | undefined,
      answer: (raw['answer'] ?? raw['value'] ?? null) as string | string[] | null,
      answerLabel: (raw['answerLabel'] as string | null) ?? null,
      answerLabels: (raw['answerLabels'] as string | string[] | null) ?? null,
      dsLabel: raw['dsLabel'] as string | undefined,
      options: rawOptions?.map(opt => ({
        qlabel: (opt['qlabel'] ?? opt['label'] ?? {}) as Record<string, string>,
        qvalue: ((opt['qvalue'] ?? opt['value']) as string) ?? '',
        dsLabel: opt['dsLabel'] as string | undefined,
      })),
      validation: raw['validation'] as SurveyQuestion['validation'],
    };
  }

  prefillFormFromQuestions(questions: SurveyHistoryQuestion[]): void {
    const patch: Record<string, string | string[] | Date | null> = {};
    questions.forEach(q => {
      if (q.answer === undefined || q.answer === null) return;
      if (q.qtype === 'date' && typeof q.answer === 'string') {
        const d = new Date(q.answer);
        patch[q.qid] = isNaN(d.getTime()) ? null : d.toISOString().split('T')[0];
      } else {
        patch[q.qid] = q.answer;
      }
    });
    this.form.patchValue(patch, { emitEvent: false });
  }

  private resolveAnswerLabel(q: SurveyQuestion, answer: string | string[] | null, locale: string): string | null {
    if (answer === null || answer === undefined) return null;
    if (!q.options?.length) return Array.isArray(answer) ? answer.join(', ') : answer;
    if (Array.isArray(answer)) {
      return answer.map(v => {
        const opt = q.options!.find(o => o.qvalue === v);
        return opt ? (opt.qlabel?.[locale] ?? opt.qlabel?.['en'] ?? v) : v;
      }).join(', ');
    }
    const opt = q.options.find(o => o.qvalue === answer);
    return opt ? (opt.qlabel?.[locale] ?? opt.qlabel?.['en'] ?? answer) : answer;
  }

  private resolveAnswerLabels(q: SurveyQuestion, answer: string | string[] | null, locale: string): string | string[] | null {
    if (answer === null || answer === undefined) return null;
    if (!q.options?.length) return answer;
    if (Array.isArray(answer)) {
      return answer.map(v => {
        const opt = q.options!.find(o => o.qvalue === v);
        return opt ? (opt.qlabel?.[locale] ?? opt.qlabel?.['en'] ?? v) : v;
      });
    }
    const opt = q.options.find(o => o.qvalue === answer);
    return opt ? (opt.qlabel?.[locale] ?? opt.qlabel?.['en'] ?? answer) : answer;
  }

  getAnswerLabel(q: SurveyQuestion): string {
    const hq = q as SurveyHistoryQuestion;
    if (Array.isArray(hq.answerLabels)) return hq.answerLabels.join(', ');
    if (hq.answerLabels) return hq.answerLabels as string;
    if (hq.answerLabel) return hq.answerLabel;
    const raw = this.form.get(q.qid)?.value;
    if (!raw) return '';
    return Array.isArray(raw) ? raw.join(', ') : String(raw);
  }

  private getGoogleTranslateLang(): string | null {
    const match = document.cookie.match(/googtrans=\/[^/]+\/([^;]+)/);
    return match ? match[1].toLowerCase() : null;
  }

  getLabel(labelObj: Record<string, string>): string {
    if (!labelObj) return '';
    const locale = this.getGoogleTranslateLang() ?? 'en';
    if (labelObj[locale]) return labelObj[locale];
    const base = locale.split('-')[0];
    if (labelObj[base]) return labelObj[base];
    return labelObj['en'] || '';
  }

  getQuestionLabel(q: SurveyQuestion): string {
    if (this.mode === 'read') {
      const hq = q as SurveyHistoryQuestion;
      if (hq.dsLabel) return hq.dsLabel;
    }
    return this.getLabel(q.qlabel);
  }

  getOptLabel(opt: SurveyOption): string {
    if (this.mode === 'read' && opt.dsLabel) return opt.dsLabel;
    const labelObj = (opt.qlabel ?? opt.label) as Record<string, string>;
    return this.getLabel(labelObj);
  }

  getOptValue(opt: SurveyOption): string {
    return (opt.qvalue ?? opt.value) as string;
  }

  getPlaceholder(q: SurveyQuestion): string {
    if (q.placeholder) return this.getLabel(q.placeholder);
    switch (q.qtype) {
      case 'text': return 'Enter value';
      case 'textarea': return 'Enter details';
      case 'select': return 'Select';
      case 'multiselect': return 'Select';
      default: return '';
    }
  }

  onMultiCheckboxChange(qid: string, value: string, checked: boolean) {
    const control = this.form.get(qid);
    const current: string[] = control?.value || [];
    if (checked) {
      control?.setValue([...current, value]);
    } else {
      control?.setValue(current.filter((v) => v !== value));
    }
    this.cdr.detectChanges();
  }

  private buildValidators(q: SurveyQuestion): ValidatorFn[] {
    const validators: ValidatorFn[] = [];
    if (q.required) validators.push(Validators.required);
    if (q.validation?.maxLength) validators.push(Validators.maxLength(+q.validation.maxLength));
    if (q.validation?.minLength && +q.validation.minLength > 0) {
      validators.push(Validators.minLength(+q.validation.minLength));
    }
    if (q.qtype === 'date' && (q.validation?.minAge !== undefined || q.validation?.maxAge !== undefined)) {
      validators.push(this.ageRangeValidator(q.validation.minAge, q.validation.maxAge));
    }
    if (q.qtype === 'date' && (q.validation?.minDate || q.validation?.maxDate)) {
      validators.push(this.dateStringRangeValidator(
        this.parseDateString(q.validation.minDate),
        this.parseDateString(q.validation.maxDate)
      ));
    }
    if (q.validation?.format) {
      validators.push(this.formatValidator(q.validation.format, q.validation.pattern));
    }
    return validators;
  }

  private formatValidator(format: string, pattern?: string): ValidatorFn {
    const DEFAULT_EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    const DEFAULT_MOBILE_RE = /^[0-9+() -]{7,20}$/;
    return (control) => {
      if (!control.value) return null;
      const v = control.value as string;
      switch (format.toUpperCase()) {
        case 'EMAIL': {
          const re = pattern ? new RegExp(pattern) : DEFAULT_EMAIL_RE;
          return re.test(v) ? null : { invalidEmail: true };
        }
        case 'MOBILE': {
          const re = pattern ? new RegExp(pattern) : DEFAULT_MOBILE_RE;
          return re.test(v) ? null : { invalidMobile: true };
        }
        case 'EMAIL_OR_MOBILE': {
          const emailOk = DEFAULT_EMAIL_RE.test(v);
          const mobileOk = (pattern ? new RegExp(pattern) : DEFAULT_MOBILE_RE).test(v);
          return (emailOk || mobileOk) ? null : { invalidEmailOrMobile: true };
        }
        default:
          return null;
      }
    };
  }

  private dateStringRangeValidator(minDate?: Date, maxDate?: Date): ValidatorFn {
    return (control) => {
      if (!control.value) return null;
      const date = new Date(control.value + 'T00:00:00');
      if (isNaN(date.getTime())) return { invalidDate: true };
      if (minDate) {
        const min = new Date(minDate);
        min.setHours(0, 0, 0, 0);
        if (date < min) return { minDate: { required: min.toISOString().split('T')[0] } };
      }
      if (maxDate) {
        const max = new Date(maxDate);
        max.setHours(23, 59, 59, 999);
        if (date > max) return { maxDate: { required: max.toISOString().split('T')[0] } };
      }
      return null;
    };
  }

  private ageRangeValidator(minAge?: number, maxAge?: number): ValidatorFn {
    return (control) => {
      if (!control.value) return null;
      const dob = new Date(control.value);
      if (isNaN(dob.getTime())) return null;
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const m = today.getMonth() - dob.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
      if (minAge !== undefined && age < minAge) {
        return { minAge: { required: minAge } };
      }
      if (maxAge !== undefined && age > maxAge) {
        return { maxAge: { required: maxAge } };
      }
      return null;
    };
  }

  private parseDateString(dateStr?: string): Date | undefined {
    if (!dateStr) return undefined;
    if (dateStr === 'TODAY') return new Date();
    const match = dateStr.match(/^TODAY([+-])(\d+)([YMD])$/i);
    if (match) {
      const sign = match[1] === '-' ? -1 : 1;
      const amount = parseInt(match[2], 10) * sign;
      const unit = match[3].toUpperCase();
      const d = new Date();
      if (unit === 'Y') d.setFullYear(d.getFullYear() + amount);
      else if (unit === 'M') d.setMonth(d.getMonth() + amount);
      else if (unit === 'D') d.setDate(d.getDate() + amount);
      return d;
    }
    return undefined;
  }

  getDateMin(q: SurveyQuestion): Date | undefined {
    return this.parseDateString(q.validation?.minDate);
  }

  getDateMax(q: SurveyQuestion): Date | undefined {
    return this.parseDateString(q.validation?.maxDate);
  }

  getDateMinStr(q: SurveyQuestion): string | undefined {
    const d = this.getDateMin(q);
    return d ? d.toISOString().split('T')[0] : undefined;
  }

  getDateMaxStr(q: SurveyQuestion): string | undefined {
    const d = this.getDateMax(q);
    return d ? d.toISOString().split('T')[0] : undefined;
  }

  buildForm() {
    this.form = this.fb.group({});
    this.parsedQuestions.forEach((q: SurveyQuestion) => {
      const isArrayType = ['checkbox', 'multiCheckbox', 'multiselect'].includes(q.qtype);
      this.form.addControl(
        q.qid,
        this.fb.control(isArrayType ? [] : null, this.buildValidators(q))
      );
    });
  }

  getOptions(q: SurveyQuestion): { label: string; value: string }[] {
    return (q.options ?? []).map(opt => ({
      label: this.getLabel((opt.qlabel ?? opt.label) as Record<string, string>),
      value: (opt.qvalue ?? opt.value) as string,
    }));
  }

  getInlineQuestions(parentQid: string, optionValue: string): SurveyQuestion[] {
    return this.parsedQuestions.filter(
      q => q.visibleif?.qid === parentQid && q.visibleif?.value === optionValue
    );
  }

  getSubQuestions(parentQid: string): SurveyQuestion[] {
    return this.parsedQuestions.filter(q => q.visibleif?.qid === parentQid);
  }

  isInlineQuestion(q: SurveyQuestion): boolean {
    if (!q.visibleif) return false;
    const parent = this.parsedQuestions.find(p => p.qid === q.visibleif?.qid);
    return !!parent;
  }

  hasError(qid: string): boolean {
    const control = this.form.get(qid);
    if (!control?.errors) return false;
    if (control.errors['required']) return this.submittedQids.has(qid);
    return !!(control.errors['maxlength'] || control.errors['minlength'] || control.errors['minAge'] || control.errors['maxAge'] || control.errors['minDate'] || control.errors['maxDate'] || control.errors['invalidDate'] || control.errors['invalidEmail'] || control.errors['invalidMobile'] || control.errors['invalidEmailOrMobile']);
  }

  getErrorMessage(qid: string, validation?: SurveyQuestion['validation']): string {
    const control = this.form.get(qid);
    if (!control?.errors) return '';
    if (control.errors['required']) return 'This field is required.';
    if (control.errors['invalidDate']) return 'Please enter a valid date.';
    if (control.errors['minDate']) {
      const match = validation?.minDate?.match(/TODAY-(\d+)Y/i);
      const years = match ? match[1] : null;
      return years ? `Date cannot be more than ${years} years in the past.` : 'Date is out of the allowed range.';
    }
    if (control.errors['maxDate']) return 'Future date cannot be selected.';
    if (control.errors['minAge']) {
      return `Age must be at least ${control.errors['minAge'].required} years.`;
    }
    if (control.errors['maxAge']) {
      return `Age cannot exceed ${control.errors['maxAge'].required} years.`;
    }
    if (control.errors['maxlength']) {
      return `This field can contain up to ${validation?.maxLength} characters.`;
    }
    if (control.errors['minlength']) {
      return `Please enter at least ${validation?.minLength} characters.`;
    }
    if (control.errors['invalidEmail']) return 'Please enter a valid email address.';
    if (control.errors['invalidMobile']) return 'Please enter a valid mobile number.';
    if (control.errors['invalidEmailOrMobile']) return 'Please enter a valid email address or mobile number.';
    if (control.errors['pattern']) return 'Invalid format.';
    return '';
  }

  isVisible(q: SurveyQuestion): boolean {
    if (this.mode === 'read') {
      const hq = q as SurveyHistoryQuestion;
      if (hq.wasVisible === false) return false;
      const a = hq.answer;
      return a !== null && a !== undefined && a !== '' && !(Array.isArray(a) && a.length === 0);
    }
    if (!q.visibleif) return true;
    const parentValue = this.form.get(q.visibleif.qid)?.value;
    if (Array.isArray(parentValue)) {
      return parentValue.includes(q.visibleif.value);
    }
    return parentValue === q.visibleif.value;
  }

  handleConditionalLogic() {
    this.parsedQuestions.forEach((q: SurveyQuestion) => {
      const control = this.form.get(q.qid);
      if (!this.isVisible(q)) {
        const isArrayType = ['checkbox', 'multiCheckbox', 'multiselect'].includes(q.qtype);
        control?.setValue(isArrayType ? [] : null, { emitEvent: false });
        control?.clearValidators();
      } else {
        control?.setValidators(this.buildValidators(q));
      }
      control?.updateValueAndValidity({ emitEvent: false });
    });
  }

  submit() {
    this.submittedQids = new Set(
      this.parsedQuestions.filter(q => this.isVisible(q)).map(q => q.qid)
    );
    this.form.markAllAsTouched();
    this.cdr.detectChanges();
    if (this.form.invalid) return;

    const locale = this.getGoogleTranslateLang() ?? 'en';
    const srvyRespJsonStr = JSON.stringify(this.parsedQuestions.map((q: SurveyQuestion) => {
      const wasVisible = this.isVisible(q);
      const rawAnswer = wasVisible ? (this.form.get(q.qid)?.value ?? null) : null;
      const answerLabel = this.resolveAnswerLabel(q, rawAnswer, locale);
      const answerLabels = this.resolveAnswerLabels(q, rawAnswer, locale);
      return {
        qid: q.qid,
        parentQId: q.parentQId || null,
        visibleif: q.visibleif ?? null,
        qtype: q.qtype,
        dsLabel: this.getLabel(q.qlabel),
        wasVisible,
        answer: rawAnswer,
        answerLabel,
        answerLabels,
        required: q.required,
        label: q.qlabel,
        validation: q.validation ?? null,
        ...(q.options ? {
          options: q.options.map(opt => {
            const labelObj = (opt.qlabel ?? opt.label) as Record<string, string>;
            return {
              value: opt.qvalue ?? opt.value,
              label: labelObj,
              dsLabel: this.getLabel(labelObj),
            };
          })
        } : {}),
      };
    }));
    const payload = {
      srvyId: this.surveyData!.srvyId,
      recordId: this.surveyData!.recordId,
      userType: this.surveyData!.userType,
      lang: locale,
      srvyRespJson: btoa(encodeURIComponent(srvyRespJsonStr).replace(/%([0-9A-F]{2})/gi, (_, p1) => String.fromCharCode(parseInt(p1, 16)))),
    };

    this.sharedService.submitSurvey(payload).subscribe(() => {
      this.surveySubmitted.emit();
    });
  }
}
